import React from 'react';
import { Box } from '@osmosis/react-ui/components/Box';
import { Flex } from '@osmosis/react-ui/components/Box';
import { Icon } from '@osmosis/react-ui/components/Icon';
import { useColors } from '@osmosis/react-ui/hooks/useColors';
import { faCircleDot } from '@fortawesome/pro-regular-svg-icons/faCircleDot';
import { faCircle } from '@fortawesome/pro-regular-svg-icons/faCircle';
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons/faCircleCheck';
import { useQuizContext } from '../../../../context/QuizContext';
import {
  OrderedResponseData,
  OrderedResponseSubmittedAnswerType,
} from '../../../../types/quiz';

const GridItem = ({
  col,
  row,
  checkedOptions,
  correctOptions,
}: {
  col: number;
  row: number;
  checkedOptions: OrderedResponseSubmittedAnswerType;
  correctOptions: OrderedResponseSubmittedAnswerType;
}) => {
  const { isAnswered } = useQuizContext();

  const colors = useColors();

  if (isAnswered && correctOptions[row] === col) {
    return <Icon icon={faCircleCheck} color={colors.positive} />;
  }

  if (checkedOptions[row] === col) {
    return <Icon icon={faCircleDot} color={colors.primary} />;
  }

  return <Icon icon={faCircle} color={colors.gray40} />;
};

/**
 * Answers list for ordered response.
 */
const OrderedResponseAnswersGrid = ({
  row,
  checkedOptions,
  setCheckedOptions,
  correctOptions,
}: {
  row: number;
  checkedOptions: OrderedResponseSubmittedAnswerType;
  correctOptions: OrderedResponseSubmittedAnswerType;
  setCheckedOptions: React.Dispatch<
    React.SetStateAction<OrderedResponseSubmittedAnswerType>
  >;
}) => {
  const { currentItem, isAnswered } = useQuizContext();

  const currentData = currentItem?.data as OrderedResponseData;

  const handleClick = (col: number) => {
    if (!isAnswered) setCheckedOptions((prev) => ({ ...prev, [row]: col }));
  };

  return (
    <Flex>
      {currentData.answers.map(({ correctValue }, index) => (
        <Box
          key={`${currentItem?.nodeID}-${correctValue}`}
          onClick={() => handleClick(index)}
          width={40}
          color="gray60"
          fontSize="18px"
          sx={{ cursor: 'pointer' }}
          data-testid={`option${index}${row}`}
        >
          <GridItem
            col={index}
            row={row}
            correctOptions={correctOptions}
            checkedOptions={checkedOptions}
          />
        </Box>
      ))}
    </Flex>
  );
};

export default OrderedResponseAnswersGrid;
